/**
 * CRUD admin — actividades_estudyoo y juegos_estudyoo (Django).
 */
import axios from 'axios';
import { getDjangoAdminToken } from '@/services/djangoAdminAuth';
import {
  catalogCacheKey,
  getCachedCatalog,
  setCachedCatalog,
} from '@/services/estudyooCatalogCache';
import {
  ADMIN_COLEGIOS_URLS,
  ADMIN_REPORTE_URLS,
  ADMIN_RESUMEN_URLS,
  fetchReporteSyncFallback,
  fetchResumenFallback,
  getAdminJson,
  getColegiosAdmin,
} from '@/services/estudyooStatsFallback';

const BACK = (process.env.VUE_APP_BACK_URL || '').replace(/\/$/, '');
const ACT = `${BACK}/actividades-estudyoo`;
const JUE = `${BACK}/juegos-estudyoo`;
const SIMCE = `${BACK}/simce`;

function headers(json = true) {
  const h = { Accept: 'application/json' };
  const t = getDjangoAdminToken();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

function list(url, params) {
  return axios.get(url, { headers: headers(), params }).then((r) => {
    const data = r.data;
    return Array.isArray(data) ? data : data.results || [];
  });
}

function getJson(url, params) {
  return axios.get(url, { headers: headers(), params }).then((r) => r.data);
}

function create(url, payload) {
  return axios.post(url, payload, { headers: headers() }).then((r) => r.data);
}

function patch(url, payload) {
  return axios.patch(url, payload, { headers: headers() }).then((r) => r.data);
}

function destroy(url) {
  return axios.delete(url, { headers: headers() }).then((r) => r.data);
}

function cachedList(name, url, params = {}, opts = {}) {
  const key = catalogCacheKey(name, params);
  if (!opts.force) {
    const hit = getCachedCatalog(key);
    if (hit !== undefined) return Promise.resolve(hit);
  }
  return list(url, params).then((data) => {
    setCachedCatalog(key, data);
    return data;
  });
}

// —— Actividades ——
export const ActividadesApi = {
  listColegios: (p) => getAdminJson(ADMIN_COLEGIOS_URLS, p, () => getColegiosAdmin()),
  getResumen: (p) => getAdminJson(ADMIN_RESUMEN_URLS, p, fetchResumenFallback),
  getReporteSync: () => getAdminJson(ADMIN_REPORTE_URLS, {}, fetchReporteSyncFallback),
  getMapaCapitulos: (p) => getJson(`${ACT}/admin/mapa-capitulos/`, p),
  listMaterias: (p = {}, opts = {}) => cachedList('materias', `${ACT}/materias/`, p, opts),
  getMateria: (id) => getJson(`${ACT}/materias/${id}/`),
  getCapitulo: (id) => getJson(`${ACT}/capitulos/${id}/`),
  listEjes: (p) => list(`${ACT}/ejes-tematicos/`, p),
  listCapitulos: (p) => list(`${ACT}/capitulos/`, p),
  listTipos: (p = {}, opts = {}) => cachedList('tipos', `${ACT}/tipos-actividad/`, p, opts),
  listActividades: (p) => list(`${ACT}/actividades/`, p),
  getActividad: (id) => getJson(`${ACT}/actividades/${id}/`),
  listPreguntasActividad: (p) => list(`${ACT}/preguntas-actividad/`, p),
  listMaterialesActividad: (p) => list(`${ACT}/materiales-actividad/`, p),
  listMaterialesCapitulo: (p) => list(`${ACT}/materiales/`, p),
  listAlternativas: (p) => list(`${ACT}/alternativas-actividad/`, p),
  listAsignaciones: (p) => list(`${ACT}/asignaciones-curso/`, p),
  listAsignacionesPage(params = {}) {
    return axios.get(`${ACT}/asignaciones-curso/`, { headers: headers(), params }).then((r) => {
      const d = r.data;
      if (d && Array.isArray(d.results)) return d;
      const arr = Array.isArray(d) ? d : [];
      return { count: arr.length, page: 1, page_size: arr.length || 20, results: arr };
    });
  },
  listCursos: (p = {}, opts = {}) => cachedList('cursos', `${SIMCE}/cursos/`, p, opts),
  listCasas: (p) => list(`${ACT}/casas/`, p),
  createActividad: (payload) => create(`${ACT}/actividades/`, payload),
  createPregunta: (payload) => create(`${ACT}/preguntas-actividad/`, payload),
  createAlternativa: (payload) => create(`${ACT}/alternativas-actividad/`, payload),
  patchActividad: (id, payload) => patch(`${ACT}/actividades/${id}/`, payload),
  patchCapitulo: (id, payload) => patch(`${ACT}/capitulos/${id}/`, payload),
  patchMateria: (id, payload) => patch(`${ACT}/materias/${id}/`, payload),
  patchPregunta: (id, payload) => patch(`${ACT}/preguntas-actividad/${id}/`, payload),
  patchAlternativa: (id, payload) => patch(`${ACT}/alternativas-actividad/${id}/`, payload),
  deletePregunta: (id) => destroy(`${ACT}/preguntas-actividad/${id}/`),
  deleteAlternativa: (id) => destroy(`${ACT}/alternativas-actividad/${id}/`),
  deleteMaterialActividad: (id) => destroy(`${ACT}/materiales-actividad/${id}/`),
  patchMaterialActividad: (id, payload) => patch(`${ACT}/materiales-actividad/${id}/`, payload),
  patchMaterialCapitulo: (id, payload) => patch(`${ACT}/materiales/${id}/`, payload),
  createAsignacion: (payload) => create(`${ACT}/asignaciones-curso/`, payload),
  patchAsignacion: (id, payload) => patch(`${ACT}/asignaciones-curso/${id}/`, payload),
  deleteAsignacion: (id) =>
    axios.delete(`${ACT}/asignaciones-curso/${id}/`, { headers: headers() }).then((r) => r.data),
  descargarPlantillaActividades() {
    return axios.get(`${ACT}/admin/plantilla-actividades.xlsx`, {
      headers: headers(false),
      responseType: 'blob',
    });
  },
  importarActividades(archivo, colegioId) {
    const fd = new FormData();
    fd.append('archivo', archivo);
    if (colegioId) fd.append('colegio_id', colegioId);
    return axios.post(`${ACT}/admin/importar-actividades/`, fd, {
      headers: { ...headers(false), 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  descargarPlantillaMateriales() {
    return axios.get(`${ACT}/admin/plantilla-materiales.xlsx`, {
      headers: headers(false),
      responseType: 'blob',
    });
  },
  importarMateriales(archivo, colegioId) {
    const fd = new FormData();
    fd.append('archivo', archivo);
    if (colegioId) fd.append('colegio_id', colegioId);
    return axios.post(`${ACT}/admin/importar-materiales/`, fd, {
      headers: { ...headers(false), 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  generarConGrok(payload) {
    return axios.post(`${ACT}/admin/generar-grok/`, payload, { headers: headers() }).then((r) => r.data);
  },
  obtenerPromptActividad(payload) {
    return axios.post(`${ACT}/admin/prompt-actividad/`, payload, { headers: headers() }).then((r) => r.data);
  },
  grokStatus() {
    return axios.get(`${ACT}/admin/grok-status/`, { headers: headers() }).then((r) => r.data);
  },
  importarGrokJson(borrador, colegioId, metaNivelCapitulo) {
    return axios.post(
      `${ACT}/admin/importar-grok-json/`,
      {
        borrador,
        ...(colegioId ? { colegio_id: colegioId } : {}),
        ...(metaNivelCapitulo ? { meta_nivel_capitulo: metaNivelCapitulo } : {}),
      },
      { headers: headers() },
    ).then((r) => r.data);
  },
  crearMaterialCapitulo(payload) {
    return create(`${ACT}/materiales/`, payload);
  },
  crearMaterialActividad(payload) {
    return create(`${ACT}/materiales-actividad/`, payload);
  },
  subirImagenMaterial(entidad, id, archivo, opts = {}) {
    const fd = new FormData();
    fd.append('entidad', entidad);
    if (id != null && id !== '') fd.append('id', id);
    fd.append('archivo', archivo);
    if (opts.ruta) fd.append('ruta', opts.ruta);
    return axios.post(`${ACT}/admin/subir-imagen/`, fd, {
      headers: { ...headers(false), 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  /** Sube imagen y solo devuelve URL (útil en borrador antes de guardar). */
  subirImagenAsset(archivo) {
    return this.subirImagenMaterial('asset', null, archivo);
  },
};

// —— Juegos ——
export const JuegosApi = {
  listPreguntas: (p) => list(`${JUE}/preguntas/`, p),
  listAlternativas: (p) => list(`${JUE}/alternativas/`, p),
  listTiposJuego: () => list(`${JUE}/tipos-juego/`),
  listSalas: (p) => list(`${JUE}/salas/`, p),
  createPregunta: (payload) => create(`${JUE}/preguntas/`, payload),
  createSala: (payload) => create(`${JUE}/salas/`, payload),
  descargarPlantillaPreguntas() {
    return axios.get(`${JUE}/admin/plantilla-preguntas.xlsx`, {
      headers: headers(false),
      responseType: 'blob',
    });
  },
  importarPreguntas(archivo, colegioId) {
    const fd = new FormData();
    fd.append('archivo', archivo);
    if (colegioId) fd.append('colegio_id', colegioId);
    return axios.post(`${JUE}/admin/importar-preguntas/`, fd, {
      headers: { ...headers(false), 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
};

export function descargarBlob(response, nombreArchivo) {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  a.click();
  window.URL.revokeObjectURL(url);
}
